import React, { useState } from "react";
import { T, AWARD_COLORS } from "./theme";
import { Icon } from "./Icons";

// ─── Botão ────────────────────────────────────────────────────────────
export function Btn({ children, variant = "primary", icon, style = {}, disabled, ...props }) {
  const V = {
    primary: { bg: T.gradient.brandCta, bgH: T.brandHi, c: T.textInv, br: T.brandHi, shadow: T.shadow.brand },
    ghost:   { bg: "transparent",       bgH: T.card,    c: T.text,    br: T.borderMid, shadow: "none" },
    subtle:  { bg: T.cardSoft,          bgH: T.cardHi,  c: T.text,    br: T.border, shadow: "none" },
    danger:  { bg: "transparent",       bgH: T.errSoft, c: T.err,     br: T.err + "66", shadow: "none" },
    success: { bg: T.ok,                bgH: "#558d68", c: T.textInv, br: T.ok, shadow: T.shadow.sm },
  }[variant];

  const [h, setH] = useState(false);
  return (
    <button
      {...props}
      disabled={disabled}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: disabled ? T.cardSoft : (h && variant !== "primary" ? V.bgH : V.bg),
        color: disabled ? T.textDim : V.c,
        border: `1px solid ${disabled ? T.border : V.br}`,
        borderRadius: T.r.md,
        padding: "9px 16px",
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        letterSpacing: 0.3,
        transition: `all 160ms ${T.ease}`,
        whiteSpace: "nowrap",
        boxShadow: disabled ? "none" : (h ? T.shadow.md : V.shadow),
        transform: h && !disabled && variant === "primary" ? "translateY(-1px)" : "none",
        ...style,
      }}
    >
      {icon} {children}
    </button>
  );
}

// ─── Input ────────────────────────────────────────────────────────────
export function Input({ label, hint, style = {}, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label style={{ fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0.8, textTransform: "uppercase" }}>
          {label}
        </label>
      )}
      <input
        {...props}
        onFocus={e => { e.target.style.borderColor = T.brand; e.target.style.boxShadow = `0 0 0 3px ${T.brand}15`; props.onFocus?.(e); }}
        onBlur={e => { e.target.style.borderColor = T.borderMid; e.target.style.boxShadow = "none"; props.onBlur?.(e); }}
        style={{
          background: T.input, border: `1px solid ${T.borderMid}`, borderRadius: T.r.md,
          padding: "10px 12px", color: T.text, fontSize: 13,
          outline: "none", width: "100%", boxSizing: "border-box",
          transition: `all 160ms ${T.ease}`,
          ...style,
        }}
      />
      {hint && <span style={{ fontSize: 11, color: T.textDim }}>{hint}</span>}
    </div>
  );
}

export function Textarea({ label, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label style={{ fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0.8, textTransform: "uppercase" }}>
          {label}
        </label>
      )}
      <textarea
        {...props}
        onFocus={e => { e.target.style.borderColor = T.brand; e.target.style.boxShadow = `0 0 0 3px ${T.brand}15`; }}
        onBlur={e => { e.target.style.borderColor = T.borderMid; e.target.style.boxShadow = "none"; }}
        style={{
          background: T.input, border: `1px solid ${T.borderMid}`, borderRadius: T.r.md,
          padding: "10px 12px", color: T.text, fontSize: 13,
          outline: "none", width: "100%", boxSizing: "border-box",
          resize: "vertical", minHeight: 80, lineHeight: 1.6,
          transition: `all 160ms ${T.ease}`,
          ...props.style,
        }}
      />
    </div>
  );
}

export function Sel({ label, children, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label style={{ fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0.8, textTransform: "uppercase" }}>
          {label}
        </label>
      )}
      <select
        {...props}
        style={{
          background: T.input, border: `1px solid ${T.borderMid}`, borderRadius: T.r.md,
          padding: "10px 12px", color: T.text, fontSize: 13,
          outline: "none", width: "100%", cursor: "pointer", ...props.style,
        }}
      >{children}</select>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────
export function Card({ children, style = {}, soft = false, ...props }) {
  return (
    <div {...props} style={{
      background: soft ? T.cardSoft : T.card,
      border: `1px solid ${T.border}`,
      borderRadius: T.r.lg,
      padding: 20,
      boxShadow: T.shadow.md,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────
export function Badge({ text, tone = "neutral", icon }) {
  const M = {
    alta: T.err, media: T.warn, baixa: T.ok,
    aberto: T.info, "em andamento": T.warn, resolvido: T.ok,
    neutral: T.textSub, brand: T.brand,
    gold: T.gold, ok: T.ok, err: T.err, info: T.info, warn: T.warn,
    // awards
    cafe: T.textSub, bronze: T.bronze, silver: T.silver,
    golden: T.gold, platinum: T.platinum, diamond: T.diamond,
  };
  const c = M[text?.toLowerCase?.()] || M[tone] || T.textSub;
  return (
    <span style={{
      background: c + "14",
      color: c,
      border: `1px solid ${c}33`,
      borderRadius: 3,
      padding: "2px 7px",
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
    }}>
      {icon} {text}
    </span>
  );
}

// ─── Pílula combinada: nome do award + emoji do time (uma pílula só) ─
export function PilulaAwardTime({ awardId, timeEmoji, size = "md", founder = false }) {
  if (founder) {
    const s = size === "xs" ? { fs: 9, pad: "1px 8px" } : size === "sm" ? { fs: 10, pad: "2px 9px" } : { fs: 11, pad: "3px 11px" };
    return (
      <span style={{
        background: "#1a140f", color: "#d7be7c",
        border: "1px solid #d7be7c66", borderRadius: 100,
        padding: s.pad, fontSize: s.fs, fontWeight: 700,
        letterSpacing: 1.2, textTransform: "uppercase",
        fontFamily: "'Fraunces', Georgia, serif", whiteSpace: "nowrap",
        display: "inline-flex", alignItems: "center", gap: 5,
      }}>
        🦁 Founder
      </span>
    );
  }
  const map = {
    cafe: "☕", bronze: "Bronze", silver: "Silver",
    gold: "Golden", platinum: "Platinum", diamond: "Diamond",
  };
  const c = AWARD_COLORS[awardId] || AWARD_COLORS.cafe;
  const sizes = {
    xs: { padding: "1px 8px", fontSize: 9,  gap: 4, emoji: 11 },
    sm: { padding: "2px 9px", fontSize: 10, gap: 5, emoji: 12 },
    md: { padding: "3px 11px", fontSize: 11, gap: 6, emoji: 14 },
  };
  const s = sizes[size] || sizes.md;
  return (
    <span style={{
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      borderRadius: 100, padding: s.padding, fontSize: s.fontSize,
      fontWeight: 600, letterSpacing: 0.3,
      fontFamily: "'Fraunces', Georgia, serif", whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: s.gap,
      lineHeight: 1.4,
    }}>
      <span>{map[awardId] || awardId}</span>
      {timeEmoji && (
        <span style={{
          fontSize: s.emoji, lineHeight: 1,
          paddingLeft: s.gap, borderLeft: `1px solid ${c.border}`,
          fontFamily: "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', system-ui, sans-serif",
        }}>{timeEmoji}</span>
      )}
    </span>
  );
}

// ─── Pílula só nome do award (sem badge image) ──────────────────────
export function PilulaAward({ awardId, size = "md" }) {
  const map = {
    cafe: "☕", bronze: "Bronze", silver: "Silver",
    gold: "Golden", platinum: "Platinum", diamond: "Diamond",
  };
  const c = AWARD_COLORS[awardId] || AWARD_COLORS.cafe;
  const sizes = {
    sm: { padding: "2px 9px", fontSize: 10 },
    md: { padding: "3px 11px", fontSize: 11 },
    lg: { padding: "4px 14px", fontSize: 13 },
  };
  const s = sizes[size] || sizes.md;
  return (
    <span style={{
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      borderRadius: 100, padding: s.padding, fontSize: s.fontSize,
      fontWeight: 600, letterSpacing: 0.3,
      fontFamily: "'Fraunces', Georgia, serif", whiteSpace: "nowrap",
      display: "inline-block", lineHeight: 1.4,
    }}>
      {map[awardId] || awardId}
    </span>
  );
}

// ─── Award Badge (texto curto, usado em linhas / chips) ──────────────
export function AwardBadge({ awardId, size = "md", withImg = false }) {
  const map = {
    cafe: { nome: "☕", color: AWARD_COLORS.cafe, img: null },
    bronze: { nome: "Bronze", color: AWARD_COLORS.bronze, img: "./badges/bronze.png" },
    silver: { nome: "Silver", color: AWARD_COLORS.silver, img: "./badges/silver.png" },
    gold: { nome: "Golden", color: AWARD_COLORS.gold, img: "./badges/gold.png" },
    platinum: { nome: "Platinum", color: AWARD_COLORS.platinum, img: "./badges/platinum.png" },
    diamond: { nome: "Diamond", color: AWARD_COLORS.diamond, img: "./badges/diamond.png" },
  };
  const d = map[awardId] || map.cafe;
  const sizes = {
    sm: { padding: withImg ? "3px 10px 3px 4px" : "3px 8px", fontSize: 10, gap: 5, icon: 10, img: 20 },
    md: { padding: withImg ? "4px 12px 4px 5px" : "5px 12px", fontSize: 12, gap: 7, icon: 12, img: 26 },
    lg: { padding: withImg ? "6px 16px 6px 7px" : "8px 16px", fontSize: 14, gap: 9, icon: 14, img: 34 },
  };
  const s = sizes[size] || sizes.md;

  return (
    <span style={{
      background: d.color.bg, color: d.color.fg,
      border: `1px solid ${d.color.border}`, borderRadius: 100,
      padding: s.padding, fontSize: s.fontSize,
      fontWeight: 600, letterSpacing: 0.3,
      display: "inline-flex", alignItems: "center", gap: s.gap,
      fontFamily: "'Fraunces', Georgia, serif",
      whiteSpace: "nowrap",
    }}>
      {withImg && d.img ? (
        <img src={d.img} alt={d.nome} style={{
          width: s.img, height: s.img, objectFit: "contain",
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
        }} />
      ) : (
        awardId !== "cafe" && <Icon.trophy size={s.icon} />
      )}
      {d.nome}
    </span>
  );
}

// ─── Award Trophy (imagem PNG do troféu físico) ──────────────────────
// Usa os PNGs oficiais em /awards/{id}.png
export function AwardTrophy({ awardId, size = 120, withLabel = false }) {
  const map = {
    cafe: null,
    bronze:   { img: "./badges/bronze.png",   label: "Bronze" },
    silver:   { img: "./badges/silver.png",   label: "Silver" },
    gold:     { img: "./badges/gold.png",     label: "Golden" },
    platinum: { img: "./badges/platinum.png", label: "Platinum" },
    diamond:  { img: "./badges/diamond.png",  label: "Diamond" },
  };
  const d = map[awardId];

  if (!d) {
    // "☕" — placeholder elegante
    return (
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: AWARD_COLORS.cafe.bg, border: `2px dashed ${AWARD_COLORS.cafe.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: AWARD_COLORS.cafe.fg, fontSize: size * 0.25,
        fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600,
      }}>☕</div>
    );
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <img
        src={d.img} alt={d.label}
        style={{
          width: size, height: "auto", objectFit: "contain",
          filter: "drop-shadow(0 4px 12px rgba(115,82,64,0.18))",
        }}
      />
      {withLabel && (
        <div className="serif" style={{ fontSize: size * 0.11, fontWeight: 600, color: AWARD_COLORS[awardId].fg, letterSpacing: 0.5 }}>
          {d.label} Award
        </div>
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, width = 480, noPadding = false }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: T.overlay,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        animation: "fadeIn 180ms ease",
      }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.card,
          border: `1px solid ${T.borderMid}`,
          borderRadius: T.r.xl,
          padding: noPadding ? 0 : 28,
          width: "100%",
          maxWidth: width,
          display: "flex", flexDirection: "column", gap: 18,
          animation: "slideUp 240ms ease",
          maxHeight: "90vh", overflowY: "auto",
          boxShadow: T.shadow.lg,
        }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: noPadding ? "24px 28px 0" : 0,
        }}>
          <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.brand, letterSpacing: -0.3 }}>{title}</div>
          <button onClick={onClose} style={{
            background: T.cardSoft, border: `1px solid ${T.border}`,
            color: T.textSub, cursor: "pointer", padding: 6,
            display: "flex", borderRadius: T.r.md,
          }}>
            <Icon.x size={16} />
          </button>
        </div>
        <div style={{ padding: noPadding ? "0 28px 28px" : 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────
export function Kpi({ label, value, sub, accent = T.brand, icon }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.lg,
      padding: "18px 20px", display: "flex", flexDirection: "column", gap: 8,
      position: "relative", overflow: "hidden",
      boxShadow: T.shadow.sm,
    }}>
      {/* Glow sutil do accent no canto superior */}
      <div style={{
        position: "absolute", top: -30, right: -30,
        width: 100, height: 100, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}22 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative" }}>
        <div style={{ fontSize: 11, color: T.textSub, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
        {icon && <div style={{ color: accent, opacity: 0.65 }}>{icon}</div>}
      </div>
      <div className="serif" style={{ fontSize: 30, fontWeight: 600, color: T.text, lineHeight: 1.1, letterSpacing: -0.5, position: "relative" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: accent, fontWeight: 500, position: "relative" }}>{sub}</div>}
      <div style={{ position: "absolute", top: 0, left: 0, width: 2, height: "100%", background: accent, opacity: 0.7 }} />
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, right, icon }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
      <div>
        <h2 className="serif" style={{
          fontSize: 30, fontWeight: 500, color: T.brand,
          margin: 0, letterSpacing: -0.5,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          {icon && <span style={{ display: "flex" }}>{icon}</span>}
          {title}
        </h2>
        {subtitle && <p style={{ color: T.textSub, fontSize: 13, margin: "6px 0 0" }}>{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────
export function Progress({ value, max, color = T.brand, height = 6, showLabel = false }) {
  const pct = Math.min((value / Math.max(max, 1)) * 100, 100);
  return (
    <div>
      <div style={{
        width: "100%", height, background: T.elev,
        borderRadius: height / 2, overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%", background: color,
          borderRadius: height / 2,
          transition: `width 800ms ${T.ease}`,
        }} />
      </div>
      {showLabel && (
        <div style={{ fontSize: 11, color: T.textDim, marginTop: 4, textAlign: "right" }}>
          {pct.toFixed(0)}%
        </div>
      )}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────
export function Avatar({ initials, size = 36, tone = T.brand, photo = null }) {
  if (photo) {
    return <img src={photo} alt={initials} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />;
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: tone + "18", border: `1px solid ${tone}44`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.34, fontWeight: 600, color: tone,
      flexShrink: 0, letterSpacing: 0.3,
    }}>{initials}</div>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────
export function Sparkline({ data, color = T.brand, w = 100, h = 28 }) {
  if (!data || data.length === 0) return null;
  const mx = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * w},${h - (v / mx) * h}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Info/note block ──────────────────────────────────────────────────
export function Note({ children, tone = "info", icon }) {
  const M = {
    info:  { bg: T.infoSoft,  fg: T.info,  border: T.info + "55" },
    ok:    { bg: T.okSoft,    fg: T.ok,    border: T.ok + "55" },
    warn:  { bg: T.warnSoft,  fg: T.warn,  border: T.warn + "55" },
    err:   { bg: T.errSoft,   fg: T.err,   border: T.err + "55" },
    brand: { bg: T.brandSoft, fg: T.brand, border: T.brand + "55" },
  };
  const d = M[tone] || M.info;
  return (
    <div style={{
      background: d.bg, border: `1px solid ${d.border}`,
      borderRadius: T.r.md, padding: "12px 14px",
      fontSize: 12, color: d.fg, lineHeight: 1.6,
      display: "flex", alignItems: "flex-start", gap: 10,
    }}>
      {icon && <div style={{ paddingTop: 1, flexShrink: 0 }}>{icon}</div>}
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}
